/**
 * 患者 API 接口
 *
 * @module patient-management/infrastructure/api
 */

import { get, post, put, del } from "@shared/domain/httpClient";
import type { HttpRequestConfig } from "@shared/http/createHttpClient";
import type {
  Patient as PatientSchema,
  PatientCreate,
  PatientUpdate,
} from "@shared/types/api";

const BASE_URL = "/patients";

/**
 * 患者 API 响应类型
 */
export interface PatientApiResponse {
  _id?: string;
  id?: string;
  patient_id?: string;
  name?: string;
  gender?: string;
  birth_date?: string;
  age?: number;
  phone?: string;
  phone_number?: string;
  email?: string;
  address?: string;
  id_card?: string;
  id_card_number?: string;
  emergency_contact?: string;
  emergency_phone?: string;
  wechat_openid?: string;
  status?: string;
  notes?: string;
  tags?: string[];
  created_at?: string;
  updated_at?: string;
}

/**
 * 创建患者请求
 * ⚠️ 字段名必须与后端 PatientCreate schema 匹配
 */
export interface CreatePatientRequest {
  name: string;
  gender: string; // 必填：男/女/未知/未说明（GB/T 2261.1）
  birth_date?: string; // 必填（由 mapper 保证）
  phone_number: string; // 必填：后端字段名
  email?: string;
  address?: string;
  id_card_number?: string; // 可选：后端字段名
  emergency_contact?: string;
  emergency_phone?: string;
  notes?: string;
  tags?: unknown[];
  tag_names?: string[];
}

/**
 * 更新患者请求
 * ⚠️ 字段名必须与后端 PatientUpdate schema 匹配
 */
export interface UpdatePatientRequest {
  name?: string;
  gender?: string;
  birth_date?: string;
  phone_number?: string; // 后端字段名
  email?: string;
  address?: string;
  id_card_number?: string; // 后端字段名
  emergency_contact?: string;
  emergency_phone?: string;
  wechat_openid?: string;
  status?: string;
  notes?: string;
  // 标签相关字段
  tags?: unknown[];
  tag_names?: string[];
  patient_type?: string;
  organization?: string;
  vip_level?: string;
}

/**
 * 患者查询参数
 */
export interface PatientQueryParams {
  keyword?: string;
  status?: string;
  patientType?: string;
  vipLevel?: string;
  organization?: string;
  tags?: string[];
  skip?: number;
  limit?: number;
  sort_by?: string;
  sort_order?: "asc" | "desc";
}

// ==================== API 函数 ====================

/**
 * 获取患者列表
 */
export async function fetchPatients(
  params?: PatientQueryParams,
): Promise<PatientApiResponse[]> {
  return get<PatientApiResponse[]>(`${BASE_URL}/`, { params });
}

/**
 * 按患者身份取主档——唯一入口。
 *
 * 同时支持 MongoDB ObjectId（患者主键）与 9 位病案号（业务编号）：24 位 hex 走
 * `/{id}`，其余走 `/seq/{id}`。历史上另有固定主键路由的 `fetchPatientById`，
 * 业务号误传进去会 100% 404（曾导致健康管理工作台姓名列全部显示为编号），
 * 已删除以消除误用面。
 *
 * @param config 可降级读取请传 `{ silent: true }`，避免主档缺失时弹全局错误。
 */
export async function fetchPatientByIdentity(
  id: string,
  config?: HttpRequestConfig,
): Promise<PatientApiResponse> {
  const path = /^[0-9a-fA-F]{24}$/.test(id)
    ? `${BASE_URL}/${encodeURIComponent(id)}`
    : `${BASE_URL}/seq/${encodeURIComponent(id)}`;
  return config ? get<PatientApiResponse>(path, config) : get<PatientApiResponse>(path);
}

/**
 * 搜索患者
 */
export async function searchPatients(
  keyword: string,
  limit: number = 20,
): Promise<PatientApiResponse[]> {
  return get<PatientApiResponse[]>(`${BASE_URL}/search`, {
    params: { keyword, limit },
  });
}

/**
 * 创建患者
 */
export async function createPatient(
  data: CreatePatientRequest,
): Promise<PatientApiResponse> {
  return post<PatientApiResponse>(`${BASE_URL}/`, data);
}

/**
 * 更新患者
 */
export async function updatePatient(
  id: string,
  data: UpdatePatientRequest,
): Promise<PatientApiResponse> {
  return put<PatientApiResponse>(`${BASE_URL}/${id}`, data);
}

/** 主档表单载荷：标准 snake_case 键（见 patientProfileForm.ts） */
export type PatientProfilePayload = Record<string, string | boolean>;

/**
 * 主档建档/改档（静默：主档校验错误由表单就地标红，不走全局提示）
 */
export async function createPatientProfile(
  payload: PatientProfilePayload,
): Promise<PatientApiResponse> {
  return post<PatientApiResponse>(`${BASE_URL}/`, payload, { silent: true });
}

export async function updatePatientProfile(
  id: string,
  payload: PatientProfilePayload,
): Promise<PatientApiResponse> {
  return put<PatientApiResponse>(
    `${BASE_URL}/${encodeURIComponent(id)}`,
    payload,
    { silent: true },
  );
}

export interface PatientFieldIssue {
  key: string;
  label: string;
}

/** GET /patients/{id}/completeness：missing 阻断（入院 L1），warnings 仅提示（值域未导入、未成年人监护人） */
export interface PatientCompleteness {
  level: "L0" | "L1";
  complete: boolean;
  missing: PatientFieldIssue[];
  warnings: PatientFieldIssue[];
}

export async function fetchPatientCompleteness(
  id: string,
  level: "L0" | "L1" = "L1",
  config?: HttpRequestConfig,
): Promise<PatientCompleteness> {
  return get<PatientCompleteness>(
    `${BASE_URL}/${encodeURIComponent(id)}/completeness`,
    { ...config, params: { level } },
  );
}

/**
 * 删除患者
 */
export async function deletePatient(id: string): Promise<{ success: boolean }> {
  return del<{ success: boolean }>(`${BASE_URL}/${id}`);
}

/**
 * 根据手机号查找患者
 */
export async function findPatientByPhone(
  phone: string,
): Promise<PatientApiResponse | null> {
  // 简单的手机号格式验证
  if (!phone || phone.length < 6) return null;

  try {
    const results = await get<PatientApiResponse[]>(`${BASE_URL}/search`, {
      params: { phone, limit: 1 },
    });
    return results.length > 0 ? results[0] : null;
  } catch {
    return null;
  }
}

/**
 * 批量根据手机号查找患者
 */
export async function findPatientsByPhones(
  phones: string[],
): Promise<Map<string, PatientApiResponse>> {
  const result = new Map<string, PatientApiResponse>();

  if (!phones || phones.length === 0) return result;

  // 过滤无效手机号
  const validPhones = phones.filter((phone) => phone && phone.length >= 6);

  if (validPhones.length === 0) return result;

  try {
    // 批量查询
    const responses = await Promise.allSettled(
      validPhones.map((phone) => findPatientByPhone(phone)),
    );

    // 处理结果
    responses.forEach((response, index) => {
      if (response.status === "fulfilled" && response.value) {
        result.set(validPhones[index], response.value);
      }
    });
  } catch (error) {
    console.error("批量查找患者失败:", error);
  }

  return result;
}

/**
 * 根据身份证查找患者
 */
export async function findPatientByIdCard(
  idCard: string,
): Promise<PatientApiResponse | null> {
  try {
    const results = await get<PatientApiResponse[]>(`${BASE_URL}/search`, {
      params: { id_card: idCard, limit: 1 },
    });
    return results.length > 0 ? results[0] : null;
  } catch {
    return null;
  }
}

/**
 * 获取患者统计信息
 */
export async function fetchPatientStats(): Promise<{
  total: number;
  active: number;
  inactive: number;
}> {
  return get(`${BASE_URL}/stats`);
}

// ==================== 试点：基于生成类型（OpenAPI）的 API 函数 ====================
//
// 以下函数与上方手写类型版本并存，复用同一 axios 客户端（运行时零差异），
// 仅把类型参数从手写 PatientApiResponse 切换为从 OpenAPI 生成的 PatientSchema。
//
// 迁移路径（渐进式）：
//   1. 当前：生成类型函数（*BySchema）与手写类型函数并存，互不影响
//   2. 验证生成类型字段推断正确后，逐步把调用方切到 *BySchema 版本
//   3. 全部切换完成、mapper 清理冗余兼容字段后，删除手写类型与旧函数
//
// 注意字段差异（codegen 暴露的真实问题）：
//   - 手写 PatientApiResponse 含冗余兼容字段：id、patient_name、phone、id_card、idCard、birthDate
//   - 生成 PatientSchema 仅含后端真实字段：_id、name、phone_number、id_card_number、birth_date
//   - mapper（patientMapper.ts）目前依赖这些兼容字段做 || 兜底，
//     切换前需先确认后端是否真的会返回非标准字段名，否则兜底逻辑可移除。

/**
 * 获取患者列表（生成类型版本）
 *
 * 返回类型为后端 Patient schema 的精确结构，字段名严格遵循 snake_case。
 */
export async function fetchPatientsBySchema(
  params?: PatientQueryParams,
): Promise<PatientSchema[]> {
  return get<PatientSchema[]>(`${BASE_URL}/`, { params });
}

/**
 * 创建患者（生成类型版本）
 *
 * 请求体类型为后端 PatientCreate schema，必填字段：name / gender / birth_date / phone_number。
 * 注意：与手写 CreatePatientRequest 相比，生成类型不含 email 字段（后端 schema 无此字段）。
 */
export async function createPatientBySchema(
  data: PatientCreate,
): Promise<PatientSchema> {
  return post<PatientSchema>(`${BASE_URL}/`, data);
}

/**
 * 更新患者（生成类型版本）
 */
export async function updatePatientBySchema(
  id: string,
  data: PatientUpdate,
): Promise<PatientSchema> {
  return put<PatientSchema>(`${BASE_URL}/${id}`, data);
}

/**
 * 类型兼容性自检（仅类型层，无运行时开销）
 *
 * 验证手写 PatientApiResponse 与生成 PatientSchema 的字段关系。
 * 若后端 schema 演进导致两者不再兼容，此处会在编译期报错，提醒开发者同步。
 */
export type PatientCompatibilityCheck = {
  // 生成类型必有的字段，手写版本应能覆盖（均为可选）
  "手写版应覆盖生成版的 _id": PatientApiResponse["_id"] extends PatientSchema["_id"]
    ? true
    : false;
  "手写版应覆盖生成版的 name": PatientApiResponse["name"] extends PatientSchema["name"]
    ? true
    : false;
  "手写版应覆盖生成版的 phone_number": PatientApiResponse["phone_number"] extends PatientSchema["phone_number"]
    ? true
    : false;
};
